import type { CreateDrinkEventRequest, DeviceIdentity } from '@lbc/shared';
import { z } from 'zod';

const DATABASE_NAME = 'lbc-local';
const DATABASE_VERSION = 5;
const DRINK_STORE_NAME = 'pending-drink-events';
const FLUP_STORE_NAME = 'pending-flup-events';
const OUTBOX_STORE_NAME = 'pending-consumption-commands';
const OUTBOX_META_STORE_NAME = 'consumption-outbox-meta';
const MUTATION_STORE_NAME = 'pending-drink-mutations';
const OUTBOX_SEQUENCE_KEY = 'next-sequence';

const queuedDrinkEventSchema = z.object({
  id: z.uuid(),
  occurredAt: z.iso.datetime({ offset: true }),
  volumeMl: z.number().int().min(10).max(5000),
  abv: z.number().min(0).max(100),
});

const pendingConsumptionCommandSchema = z.object({
  id: z.uuid(),
  sequence: z.number().int().positive(),
  installationId: z.uuid(),
  membershipIds: z.array(z.uuid()).min(1),
  event: queuedDrinkEventSchema,
});

const pendingDrinkMutationSchema = z.discriminatedUnion('operation', [
  z.object({
    eventId: z.uuid(),
    sequence: z.number().int().positive(),
    groupId: z.uuid(),
    participantId: z.uuid(),
    operation: z.literal('update'),
    occurredAt: z.iso.datetime({ offset: true }),
    volumeMl: z.number().int().min(10).max(5000),
    abv: z.number().min(0).max(100),
  }),
  z.object({
    eventId: z.uuid(),
    sequence: z.number().int().positive(),
    groupId: z.uuid(),
    participantId: z.uuid(),
    operation: z.literal('delete'),
  }),
]);

export type PendingConsumptionCommand = z.infer<typeof pendingConsumptionCommandSchema>;
export type PendingDrinkMutation = z.infer<typeof pendingDrinkMutationSchema>;
export interface ConsumptionAudience {
  installationId: string;
  membershipIds: string[];
}
/**
 * Old queue records have no audience. They can only be attached to the one
 * legacy session that was current while upgrading, never to every v3 group.
 */
export interface LegacyQueueContext {
  installationId: string;
  legacyIdentity: Pick<DeviceIdentity, 'groupId' | 'participantId'>;
  currentMembership: Pick<PendingConsumptionCommand, 'installationId'> & {
    membershipId: string;
    groupId: string;
    participantId: string;
  };
}
export interface LegacyQueueMigrationResult {
  migrated: number;
  skipped: number;
  retained: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DRINK_STORE_NAME)) {
        database.createObjectStore(DRINK_STORE_NAME, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(OUTBOX_STORE_NAME)) {
        const store = database.createObjectStore(OUTBOX_STORE_NAME, { keyPath: 'id' });
        store.createIndex('by-sequence', 'sequence', { unique: true });
      }
      if (!database.objectStoreNames.contains(OUTBOX_META_STORE_NAME)) {
        database.createObjectStore(OUTBOX_META_STORE_NAME);
      }
      if (!database.objectStoreNames.contains(MUTATION_STORE_NAME)) {
        const store = database.createObjectStore(MUTATION_STORE_NAME, { keyPath: 'eventId' });
        store.createIndex('by-sequence', 'sequence', { unique: true });
      }
      // This store is unrelated to the v3 consumption migration and remains safe to remove.
      if (database.objectStoreNames.contains(FLUP_STORE_NAME)) {
        database.deleteObjectStore(FLUP_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('IndexedDB konnte nicht geöffnet werden.'));
  });
}

function completeTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(
        transaction.error ?? new Error('Offline-Warteschlange konnte nicht gespeichert werden.'),
      );
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('Offline-Warteschlange wurde abgebrochen.'));
  });
}

function requestResult<T>(request: IDBRequest<T>, fallback: string): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(fallback));
  });
}

function parseAudience(audience: ConsumptionAudience): ConsumptionAudience | null {
  const parsed = z
    .object({
      installationId: z.uuid(),
      membershipIds: z.array(z.uuid()).min(1),
    })
    .safeParse(audience);
  if (!parsed.success) {
    return null;
  }
  return {
    installationId: parsed.data.installationId,
    membershipIds: [...new Set(parsed.data.membershipIds)],
  };
}

function audienceForLegacyContext(context: LegacyQueueContext): ConsumptionAudience | null {
  const parsed = z
    .object({
      installationId: z.uuid(),
      legacyIdentity: z.object({ groupId: z.uuid(), participantId: z.uuid() }),
      currentMembership: z.object({
        installationId: z.uuid(),
        membershipId: z.uuid(),
        groupId: z.uuid(),
        participantId: z.uuid(),
      }),
    })
    .safeParse(context);
  if (
    !parsed.success ||
    parsed.data.installationId !== parsed.data.currentMembership.installationId ||
    parsed.data.legacyIdentity.groupId !== parsed.data.currentMembership.groupId ||
    parsed.data.legacyIdentity.participantId !== parsed.data.currentMembership.participantId
  ) {
    return null;
  }
  return {
    installationId: parsed.data.installationId,
    membershipIds: [parsed.data.currentMembership.membershipId],
  };
}

async function nextSequence(metaStore: IDBObjectStore): Promise<number> {
  const stored = await requestResult<unknown>(
    metaStore.get(OUTBOX_SEQUENCE_KEY),
    'Outbox-Metadaten sind nicht lesbar.',
  );
  const sequence =
    typeof stored === 'number' && Number.isSafeInteger(stored) && stored > 0 ? stored : 1;
  metaStore.put(sequence + 1, OUTBOX_SEQUENCE_KEY);
  return sequence;
}

/**
 * Legacy API retained until all callers use consumptionOutbox. It deliberately
 * continues using the old store so a code rollback sees the same queued events.
 */
export const eventQueue = {
  async list(): Promise<CreateDrinkEventRequest[]> {
    return listEvents<CreateDrinkEventRequest>(DRINK_STORE_NAME);
  },

  async put(event: CreateDrinkEventRequest): Promise<void> {
    return putEvent(DRINK_STORE_NAME, event);
  },

  async remove(id: string): Promise<void> {
    return removeEvent(DRINK_STORE_NAME, id);
  },

  async clear(): Promise<void> {
    return clearEvents(DRINK_STORE_NAME);
  },
};

/**
 * v3 ordered outbox. Audience membership IDs are captured when the drink is
 * tapped and are never recalculated during synchronization.
 */
export const consumptionOutbox = {
  async list(): Promise<PendingConsumptionCommand[]> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(OUTBOX_STORE_NAME, 'readonly');
      const store = transaction.objectStore(OUTBOX_STORE_NAME);
      const result = await requestResult(
        store.index('by-sequence').getAll(),
        'Warteschlange ist nicht lesbar.',
      );
      await completeTransaction(transaction);
      return result
        .map((item) => pendingConsumptionCommandSchema.safeParse(item))
        .filter((item): item is z.ZodSafeParseSuccess<PendingConsumptionCommand> => item.success)
        .map((item) => item.data);
    } finally {
      database.close();
    }
  },

  async enqueue(event: CreateDrinkEventRequest, audience: ConsumptionAudience): Promise<boolean> {
    const parsedEvent = queuedDrinkEventSchema.safeParse(event);
    const parsedAudience = parseAudience(audience);
    if (!parsedEvent.success || parsedAudience === null) {
      return false;
    }

    const database = await openDatabase();
    try {
      const transaction = database.transaction(
        [OUTBOX_STORE_NAME, OUTBOX_META_STORE_NAME],
        'readwrite',
      );
      const outboxStore = transaction.objectStore(OUTBOX_STORE_NAME);
      const existing = pendingConsumptionCommandSchema.safeParse(
        await requestResult(
          outboxStore.get(parsedEvent.data.id),
          'Warteschlange ist nicht lesbar.',
        ),
      );
      if (existing.success) {
        // A merged offline tap edits the original event but must retain its frozen audience.
        outboxStore.put({ ...existing.data, event: parsedEvent.data });
      } else {
        const sequence = await nextSequence(transaction.objectStore(OUTBOX_META_STORE_NAME));
        outboxStore.add({
          id: parsedEvent.data.id,
          sequence,
          installationId: parsedAudience.installationId,
          membershipIds: parsedAudience.membershipIds,
          event: parsedEvent.data,
        } satisfies PendingConsumptionCommand);
      }
      await completeTransaction(transaction);
      return true;
    } finally {
      database.close();
    }
  },

  async remove(id: string): Promise<void> {
    return removeEvent(OUTBOX_STORE_NAME, id);
  },

  /**
   * Copies valid legacy events exactly once. The legacy store is intentionally
   * retained as rollback evidence and is never cleared by this migration.
   */
  async migrateLegacy(context: LegacyQueueContext): Promise<LegacyQueueMigrationResult> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(
        [DRINK_STORE_NAME, OUTBOX_STORE_NAME, OUTBOX_META_STORE_NAME],
        'readwrite',
      );
      const legacyEvents = await requestResult(
        transaction.objectStore(DRINK_STORE_NAME).getAll(),
        'Legacy-Warteschlange ist nicht lesbar.',
      );
      const outboxStore = transaction.objectStore(OUTBOX_STORE_NAME);
      const metaStore = transaction.objectStore(OUTBOX_META_STORE_NAME);
      const audience = audienceForLegacyContext(context);
      if (audience === null) {
        await completeTransaction(transaction);
        return { migrated: 0, skipped: legacyEvents.length, retained: legacyEvents.length };
      }
      let migrated = 0;
      let skipped = 0;
      for (const legacyEvent of legacyEvents) {
        const event = queuedDrinkEventSchema.safeParse(legacyEvent);
        if (!event.success) {
          skipped += 1;
          continue;
        }
        const existing = await requestResult<unknown>(
          outboxStore.get(event.data.id),
          'Warteschlange ist nicht lesbar.',
        );
        if (existing !== undefined) {
          skipped += 1;
          continue;
        }
        const sequence = await nextSequence(metaStore);
        outboxStore.add({
          id: event.data.id,
          sequence,
          installationId: audience.installationId,
          membershipIds: audience.membershipIds,
          event: event.data,
        } satisfies PendingConsumptionCommand);
        migrated += 1;
      }
      await completeTransaction(transaction);
      return { migrated, skipped, retained: legacyEvents.length };
    } finally {
      database.close();
    }
  },
};

/**
 * Ordered updates and deletions for events that already exist on the server.
 * A single desired state is kept per event, so repeated offline corrections
 * never create duplicate commands.
 */
export const drinkMutationQueue = {
  async list(): Promise<PendingDrinkMutation[]> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(MUTATION_STORE_NAME, 'readonly');
      const result = await requestResult(
        transaction.objectStore(MUTATION_STORE_NAME).index('by-sequence').getAll(),
        'Offline-Änderungen sind nicht lesbar.',
      );
      await completeTransaction(transaction);
      return result
        .map((item) => pendingDrinkMutationSchema.safeParse(item))
        .filter((item): item is z.ZodSafeParseSuccess<PendingDrinkMutation> => item.success)
        .map((item) => item.data);
    } finally {
      database.close();
    }
  },

  async update(event: {
    eventId: string;
    groupId: string;
    participantId: string;
    occurredAt: string;
    volumeMl: number;
    abv: number;
  }): Promise<boolean> {
    const parsed = pendingDrinkMutationSchema.safeParse({
      ...event,
      operation: 'update',
      sequence: 1,
    });
    if (!parsed.success) {
      return false;
    }
    return putMutation(parsed.data);
  },

  async delete(event: {
    eventId: string;
    groupId: string;
    participantId: string;
  }): Promise<boolean> {
    const parsed = pendingDrinkMutationSchema.safeParse({
      ...event,
      operation: 'delete',
      sequence: 1,
    });
    if (!parsed.success) {
      return false;
    }
    return putMutation(parsed.data);
  },

  async remove(eventId: string): Promise<void> {
    return removeEvent(MUTATION_STORE_NAME, eventId);
  },
};

async function putMutation(mutation: PendingDrinkMutation): Promise<boolean> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(
      [MUTATION_STORE_NAME, OUTBOX_META_STORE_NAME],
      'readwrite',
    );
    const store = transaction.objectStore(MUTATION_STORE_NAME);
    const existing = pendingDrinkMutationSchema.safeParse(
      await requestResult(store.get(mutation.eventId), 'Offline-Änderungen sind nicht lesbar.'),
    );
    const next = existing.success
      ? { ...mutation, sequence: existing.data.sequence }
      : {
          ...mutation,
          sequence: await nextSequence(transaction.objectStore(OUTBOX_META_STORE_NAME)),
        };
    store.put(next);
    await completeTransaction(transaction);
    return true;
  } finally {
    database.close();
  }
}

async function listEvents<T>(storeName: string): Promise<T[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(storeName, 'readonly');
    const request = transaction.objectStore(storeName).getAll();
    const result = await requestResult<unknown[]>(request, 'Warteschlange ist nicht lesbar.');
    await completeTransaction(transaction);
    return result as T[];
  } finally {
    database.close();
  }
}

async function putEvent<T>(storeName: string, event: T): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(storeName, 'readwrite');
    transaction.objectStore(storeName).put(event);
    await completeTransaction(transaction);
  } finally {
    database.close();
  }
}

async function removeEvent(storeName: string, id: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(storeName, 'readwrite');
    transaction.objectStore(storeName).delete(id);
    await completeTransaction(transaction);
  } finally {
    database.close();
  }
}

async function clearEvents(storeName: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(storeName, 'readwrite');
    transaction.objectStore(storeName).clear();
    await completeTransaction(transaction);
  } finally {
    database.close();
  }
}
